import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useTelegram } from '../hooks/useTelegram';

/** Deep link to the Mini App. The app only runs inside Telegram. */
const BOT_APP_URL = 'https://t.me/Asia_wallet_bot/app';

/** Centered brand spinner while the Telegram SDK is still being detected. */
function Splash() {
  return (
    <div className="grid min-h-screen place-items-center bg-surface">
      <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
    </div>
  );
}

/** Gate that only lets the wallet render inside a real Telegram client. Outside
 *  Telegram (browser, wrong host) it shows a QR + link to open the Mini App. */
export function TelegramGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { status, platform } = useTelegram();

  // Local Vite preview has no real Telegram client — mock wallets still work.
  if (import.meta.env.DEV) return <>{children}</>;

  // Still detecting the Telegram SDK — avoid flashing the gate.
  if (status === 'loading') return <Splash />;

  // A real Telegram client reports a concrete platform (ios/android/tdesktop/…).
  const inTelegram = status === 'ready' && platform !== '' && platform !== 'unknown';
  if (inTelegram) return <>{children}</>;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-surface px-8 text-center">
      <div className="flex flex-col items-center gap-3">
        <div className="grid size-16 place-items-center rounded-[20px] bg-gradient-to-br from-[#5cb6f8] to-[#2152f1] text-[28px] font-semibold text-white">
          A
        </div>
        <h1 className="text-[24px] font-medium text-ink">{t('gate.title')}</h1>
        <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">
          {t('gate.subtitle')}
        </p>
      </div>

      {/* Deliberately white in both themes: a QR scans off a light quiet zone,
          so this panel does not follow the theme. */}
      <div className="rounded-[28px] bg-white p-5 shadow-card">
        <QRCodeSVG value={BOT_APP_URL} size={200} bgColor="#ffffff" fgColor="#0e1116" level="M" />
      </div>

      <a
        href={BOT_APP_URL}
        target="_blank"
        rel="noreferrer"
        className="flex h-[56px] w-full max-w-[320px] items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:bg-accent-ink"
      >
        {t('gate.cta')}
      </a>
    </div>
  );
}
